<template>
	<DialogControl v-model="open" :title="title" :icon="icon" max-width="680" :attach="overlayTarget" body-class="item-picker__body">
		<v-text-field v-model="search" prepend-inner-icon="search" :label="t('Search')" variant="outlined" density="compact" clearable hide-details />
		<v-btn-toggle v-if="tabs && tabs.length" :model-value="tab" mandatory divided color="primary" variant="outlined" density="comfortable" class="item-picker__tabs" @update:model-value="(value) => emit('update:tab', value)">
			<v-btn v-for="item in tabs" :key="item.key" :value="item.key">{{ item.label }}</v-btn>
		</v-btn-toggle>
		<div v-if="loading" class="item-picker__empty">
			<v-progress-circular indeterminate color="primary" />
		</div>
		<div v-else-if="!filtered.length" class="item-picker__empty">{{ emptyText || t('No products match your search') }}</div>
		<v-virtual-scroll v-else :items="filtered" :height="listHeight" item-key="id" class="item-picker__list">
			<template #default="{ item }">
				<v-list-item :active="isSelected(item.id)" color="primary" rounded="lg" class="item-picker__item mb-2" @click="toggle(item.id)">
					<template #prepend>
						<v-checkbox-btn :model-value="isSelected(item.id)" color="primary" density="compact" tabindex="-1" @click.stop="toggle(item.id)" class="mr-2" />
					</template>
					<ProductLabel :params="cellParams(item)" />
				</v-list-item>
			</template>
		</v-virtual-scroll>
		<template #actions-prepend>
			<v-chip v-if="selected.length" color="primary" size="small" prepend-icon="check">{{ selected.length }}</v-chip>
		</template>
		<template #actions>
			<v-btn variant="text" @click="open = false">{{ t('Close') }}</v-btn>
			<v-btn v-if="!hideSelect" color="primary" variant="tonal" :disabled="!selected.length" @click="confirmSelection">{{ t('Select') }}</v-btn>
		</template>
	</DialogControl>
</template>

<script setup>
import { computed, inject, ref, watch } from 'vue';
import DialogControl from '@/components/controls/DialogControl.vue';
import ProductLabel from '@/components/common/ProductLabel.vue';
import { useTranslator } from '@/composables/useTranslator';
import { filterPickerItems } from '@/utils/ui/pickerSearch';

const open = defineModel({ type: Boolean, default: false });
const props = defineProps({
	title: { type: String, default: '' },
	icon: { type: String, default: 'add_shopping_cart' },
	items: { type: Array, default: () => [] },
	tabs: { type: Array, default: null },
	tab: { type: String, default: null },
	loading: { type: Boolean, default: false },
	emptyText: { type: String, default: '' },
	thumbnails: { type: Boolean, default: true },
	hideSelect: { type: Boolean, default: false },
});
const emit = defineEmits(['update:tab', 'select']);

const t = useTranslator();
const overlayTarget = inject('overlayTarget', null);
const search = ref('');
const selected = ref([]);

const filtered = computed(() => filterPickerItems(props.items, search.value));
const listHeight = computed(() => Math.min(Math.max(filtered.value.length, 1) * 64, 440));

const isSelected = (id) => selected.value.includes(id);
const subtitleOf = (item) => [item.ean, item.description].filter(Boolean).join(' · ');
const cellParams = (item) => ({ value: item.name, subtitle: subtitleOf(item), imageThumbnailUid: item.imageThumbnailUid || null, icon: props.thumbnails ? undefined : false, avatarSize: 40, tags: item.tag ? [{ text: item.tag.text, class: `text-${item.tag.color || 'primary'}` }] : [] });

function toggle(id) {
	selected.value = isSelected(id) ? selected.value.filter((value) => value !== id) : [...selected.value, id];
}

function confirmSelection() {
	emit('select', selected.value.slice());
	open.value = false;
}

watch(open, (value) => {
	if (!value) return;
	search.value = '';
	selected.value = [];
});

watch(
	() => props.tab,
	() => {
		selected.value = [];
	}
);
</script>
